/**
 * A place for a screen to say "not right now" to an automatic page reload.
 *
 * 🚨 THE ONLY THING THAT AUTOMATICALLY RELOADS THIS APP IS A DEPLOY, AND WE
 * DEPLOY ABOUT TEN TIMES A DAY.
 *
 * `UpdatePrompt` used to reload the page whenever a new service worker took
 * control, which happened within 60s of every production deploy: it polls
 * `registration.update()` on a 60s interval and again on every return to the
 * foreground, and `sw.js` called `skipWaiting()` on install, so a new worker
 * never waited — it activated, claimed the page, and `controllerchange` fired.
 *
 * Two reversals have since closed that, and both are worth knowing because the
 * second was invisible behind the first. `sw.js` no longer calls
 * `skipWaiting()` on install (the reasoning is at the top of that file), which
 * fixed the BROWSER. Native was still auto-applying — `showBar` posted
 * SKIP_WAITING itself the moment a worker installed, because there was
 * supposedly no bar to show inside a WebView — so the phone kept reloading
 * silently, unannounced, mid-shift, for as long as the browser was fine. That
 * went on 2026-09-01 alongside TKT-1159; an update now applies only when the
 * user presses Refresh, when they reopen the app after a real absence
 * (`src/lib/update-apply.ts`), or on a cold start.
 *
 * The work that reload interrupts most expensively is the onboarding
 * conversation: a live WebSocket to ElevenLabs, mid-sentence, during the one
 * session that decides whether a signup becomes a customer. From the owner's
 * seat it is indistinguishable from Mucka hanging up on him — Tom hit it on a
 * handset and reported "it disconnected randomly, I have good internet so won't
 * have been me". He was right: the close code was 1001 ("going away"), which is
 * the browser unloading the page. The ElevenLabs SDK only ever closes with
 * 1000, so a 1001 is never us hanging up and never a network fault.
 *
 * Holding does NOT skip the update. It downgrades an unannounced reload to the
 * bar that was always supposed to be the browser behaviour, and the next
 * ordinary navigation picks the new code up regardless.
 *
 * Two rails hold: the onboarding conversation, and — since TKT-1159 — the
 * in-app `VoiceEngine`, which had been missing it since the day it was written.
 */

type Release = () => void;

const holds = new Set<symbol>();
const listeners = new Set<() => void>();

/**
 * Ask for automatic reloads to be held off. Returns the release function —
 * call it when the work is finished. Releasing is idempotent, so it is safe to
 * call from a React cleanup that may run twice under StrictMode.
 */
export function holdReloads(): Release {
  const token = Symbol('reload-hold');
  holds.add(token);
  return () => {
    if (!holds.delete(token)) return;
    if (holds.size === 0) for (const fn of listeners) fn();
  };
}

/** Is anything currently asking us not to reload? */
export function reloadsHeld(): boolean {
  return holds.size > 0;
}

/** Run `fn` when the last hold is released. Returns an unsubscribe function. */
export function onReloadsReleased(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Test seam — drops every hold and listener. */
export function resetReloadGuard(): void {
  holds.clear();
  listeners.clear();
}
